import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { faX } from '@fortawesome/pro-solid-svg-icons';
import { cancelPledge } from 'api/pledges';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { GivebutterAxiosError } from 'types/error';

const CancelPledge = ({ itemId, children }: { itemId: number; children: React.ReactNode }) => {
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const toast = useToast();

    const { mutate: cancelItem, isPending: deleteLoading } = useMutation({
        mutationFn: () => {
            return cancelPledge(accountId, itemId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pledges(accountId),
                type: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
                type: 'all',
            });
            toast.success('Successfully canceled pledge!');
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data.message || (
                    <>
                        <span className={'font-medium'}>Whoops.</span> Something went wrong. Please
                        try again.
                    </>
                ),
            );
        },
    });

    return (
        <AlertDialog.Root key={key} type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    {deleteLoading && <Loader />}
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faX} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Cancel pledge?</AlertDialog.Title>
                        <AlertDialog.Description>
                            This is a permanent action and cannot be undone.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={e => {
                                e.preventDefault();
                                cancelItem();
                            }}
                        >
                            Cancel pledge
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = CancelPledge;
const CancelPledgeTrigger = AlertDialog.Trigger;
const Trigger = CancelPledgeTrigger;

export {
    Root,
    Trigger,
    //
    CancelPledge,
    CancelPledgeTrigger,
};
